import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, CreditCard, ShieldCheck } from 'lucide-react';
import { Query, RequesterInfo } from '../types';
import { pushDataLayer, getTrackingCookies } from '../utils/gtm';
import { buildFinalPayload, buildHkdkPayload } from '../utils/wompi-payload';

const WOMPI_PUBLIC_KEY = 'pub_prod_c2EdC0mYemKykMmDyYHNFYILJg9bgEni';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtCOP = (n: number) => new Intl.NumberFormat('es-CO').format(n);

async function generateIntegrityHash(reference: string, amountInCents: number): Promise<string> {
  const { data } = await axios.post(`${API_URL}/api/integrity-hash`, {
    reference,
    amount_in_cents: amountInCents,
  });
  return data.hash;
}

interface Props {
  queries: Query[];
  requesterInfo: RequesterInfo;
  onSuccess: () => void;
  onBack: () => void;
}

export default function Step3({ queries, requesterInfo, onSuccess, onBack }: Props) {
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const total = queries.reduce((s, q) => s + q.amount, 0);
  const amountInCents = total * 100;
  // Ref to know if checkout callback already fired (avoid double-reset)
  const callbackFiredRef = useRef(false);

  const handlePay = async () => {
    setLoading(true);
    setPaymentError(null);
    callbackFiredRef.current = false;

    // GTM: InitiateCheckout — user commits to paying
    pushDataLayer('InitiateCheckout', {
      currency: 'COP',
      value: total,
      user: {
        first_name: requesterInfo.firstName,
        last_name: requesterInfo.lastName,
        doc_type: requesterInfo.docType,
        doc_number: requesterInfo.docNumber,
        email: requesterInfo.email,
        phone: requesterInfo.phone,
      },
      items: queries.map(q => ({
        name: q.nombreCompleto || 'Identidad no verificada',
        doc_type: q.docType,
        doc_number: q.docNumber,
        price: q.amount,
      })),
      tracking: getTrackingCookies(),
    });

    try {
      if (!window.WidgetCheckout) {
        throw new Error('Widget de pago no disponible');
      }

      const reference = `RDB-${Date.now()}`;
      const integrityHash = await generateIntegrityHash(reference, amountInCents);

      // Persist payment data so PSE/Nequi redirect can resume the flow
      sessionStorage.setItem('wompi_pending', JSON.stringify({ reference, queries, requesterInfo }));

      const checkout = new window.WidgetCheckout({
        currency: 'COP',
        amountInCents,
        reference,
        publicKey: WOMPI_PUBLIC_KEY,
        signature: { integrity: integrityHash },
        redirectUrl: window.location.href,
        customerData: {
          email: requesterInfo.email,
          fullName: `${requesterInfo.firstName} ${requesterInfo.lastName}`,
          phoneNumber: requesterInfo.phone.replace(/\D/g, '').slice(-10),
          phoneNumberPrefix: '+57',
          legalId: requesterInfo.docNumber,
          legalIdType: requesterInfo.docType,
        },
      });

      // ─── Robust popup-close detection (two layers) ───────────────────────
      // Wompi injects DOM nodes when popup opens and removes them on close.
      // We track body.childElementCount to detect this reliably, plus a
      // postMessage listener as secondary confirmation.
      const bodyCountBefore = document.body.childElementCount;
      let wompiDetected = false;
      let cleanupDone = false;

      let pollId: ReturnType<typeof setInterval>;
      let msgHandler: (e: MessageEvent) => void;

      const resetOnClose = () => {
        if (callbackFiredRef.current || cleanupDone) return;
        cleanupDone = true;
        clearInterval(pollId);
        window.removeEventListener('message', msgHandler);
        setLoading(false);
        setPaymentError('El pago fue cerrado. Puedes intentarlo nuevamente cuando quieras.');
      };

      // Layer 1 — DOM polling every 300 ms
      pollId = setInterval(() => {
        const count = document.body.childElementCount;
        if (count > bodyCountBefore) {
          wompiDetected = true; // Wompi opened
        } else if (wompiDetected && count <= bodyCountBefore) {
          resetOnClose(); // Wompi elements gone = popup closed
        }
      }, 300);

      // Layer 2 — postMessage from Wompi iframe
      msgHandler = (e: MessageEvent) => {
        try {
          const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
          if (['CLOSE', 'close', 'CANCELLED', 'cancelled', 'dismissed'].includes(d?.type) ||
              ['CLOSE', 'CANCELLED'].includes(d?.status)) {
            resetOnClose();
          }
        } catch (_) { /* ignore parse errors */ }
      };
      window.addEventListener('message', msgHandler);

      // Safety cleanup after 10 min regardless
      const safetyTimer = setTimeout(() => {
        clearInterval(pollId);
        window.removeEventListener('message', msgHandler);
      }, 600_000);

      // ─── Official Wompi callback ──────────────────────────────────────────
      checkout.open(async (result: any) => {
        callbackFiredRef.current = true;
        clearInterval(pollId);
        clearTimeout(safetyTimer);
        window.removeEventListener('message', msgHandler);

        const transaction = result?.transaction;
        if (transaction?.status === 'APPROVED') {
          // GTM: purchase — payment confirmed
          pushDataLayer('purchase', {
            currency: 'COP',
            value: total,
            transaction_id: transaction?.id || reference,
            user: {
              first_name: requesterInfo.firstName,
              last_name: requesterInfo.lastName,
              doc_type: requesterInfo.docType,
              doc_number: requesterInfo.docNumber,
              email: requesterInfo.email,
              phone: requesterInfo.phone,
            },
            items: queries.map(q => ({
              name: q.nombreCompleto || 'Identidad no verificada',
              doc_type: q.docType,
              doc_number: q.docNumber,
              price: q.amount,
            })),
            tracking: getTrackingCookies(),
          });
          try {
            await axios.post(
              `${API_URL}/api/submit-order`,
              { data: buildFinalPayload(queries, requesterInfo, reference).data },
              { timeout: 10000 }
            );
          } catch (_) { /* webhook failure doesn't block success */ }
          // Send one hkdk order per person consulted
          const transId = transaction?.id || reference;
          await Promise.all(
            queries.map(query =>
              axios.post(
                `${API_URL}/api/submit-hkdk`,
                buildHkdkPayload(query, requesterInfo, transId),
                { timeout: 10000 }
              ).catch(() => {})
            )
          );
          onSuccess();
        } else if (transaction?.status === 'DECLINED') {
          setPaymentError('El pago fue rechazado. Por favor verifica los datos de tu tarjeta o intenta con otro medio de pago.');
          setLoading(false);
        } else if (transaction?.status === 'PENDING') {
          setPaymentError('El pago está pendiente de confirmación. Recibirás un correo cuando se confirme.');
          setLoading(false);
        } else {
          // null / undefined transaction → user closed popup (Wompi did call callback)
          setLoading(false);
          if (!cleanupDone) {
            setPaymentError('El pago fue cancelado. Puedes intentarlo nuevamente cuando quieras.');
          }
        }
      });

    } catch (err: any) {
      setPaymentError('No fue posible iniciar el proceso de pago. Por favor intenta nuevamente.');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-8 border dark-form-card"
        style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-md)' }}
      >
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
          Resumen de tu pedido
        </h2>

        {/* Order summary */}
        <div className="border rounded-2xl overflow-hidden mb-6" style={{ borderColor: 'var(--border-color)' }}>
          <div className="px-4 py-3 border-b" style={{ backgroundColor: 'var(--bg-surface-2)', borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#64748B]">
              <CreditCard size={15} />
              Detalle de consultas
            </div>
          </div>
          {queries.map((q, i) => (
            <div key={q.id} className={`px-4 py-4 ${i < queries.length - 1 ? 'border-b' : ''}`} style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--text-primary)' }}>
                    {q.nombreCompleto || 'Identidad no verificada'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                    {q.docTypeLabel} · <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{q.docNumber}</span>
                  </p>
                </div>
                <span className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-primary)' }}>
                  ${fmtCOP(q.amount)}
                </span>
              </div>
            </div>
          ))}
          <div className="px-4 py-3 border-t flex justify-between items-center" style={{ backgroundColor: 'var(--bg-surface-2)', borderColor: 'var(--border-color)' }}>
            <span className="font-bold" style={{ color: 'var(--text-primary)' }}>Total a pagar</span>
            <span className="text-xl font-bold text-[#2563EB]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ${fmtCOP(total)} COP
            </span>
          </div>
        </div>

        {/* Requester info summary */}
        <div className="rounded-xl p-4 mb-6 text-sm" style={{ backgroundColor: 'var(--bg-surface-2)', color: 'var(--text-secondary)' }}>
          <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
            {requesterInfo.firstName} {requesterInfo.lastName}
          </p>
          <p>{requesterInfo.email}</p>
          <p>{requesterInfo.phone}</p>
        </div>

        {/* Payment error */}
        <AnimatePresence>
          {paymentError && (
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="text-sm mb-4" style={{ color: '#B91C1C' }}
              data-testid="payment-error"
            >
              {paymentError}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Buttons */}
        <div className="flex gap-3">
          <motion.button
            type="button"
            data-testid="back-btn-step3"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={onBack}
            className="flex items-center gap-2 px-5 py-3 rounded-2xl border-2 font-medium transition-all"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', backgroundColor: 'var(--bg-surface)' }}
          >
            <ArrowLeft size={16} />
            Volver
          </motion.button>

          <motion.button
            data-testid="pay-btn"
            whileHover={!loading ? { scale: 1.01 } : {}}
            whileTap={!loading ? { scale: 0.99 } : {}}
            onClick={handlePay}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-white transition-all disabled:opacity-60"
            style={{ backgroundColor: '#2563EB', boxShadow: '0 4px 20px rgba(37,99,235,0.25)' }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <ShieldCheck size={18} />
                Ir al pago seguro
              </>
            )}
          </motion.button>
        </div>

        <p className="text-xs text-[#64748B] text-center mt-4 flex items-center justify-center gap-1">
          <ShieldCheck size={12} />
          Pago seguro procesado por Wompi
        </p>
      </motion.div>
    </div>
  );
}
